/* 0.47.76 - Stay in view while scrolling.
 *
 * An element (often a column group or an Information card with a
 * calculation) set to "Stay in view" follows the screen while the page
 * scrolls, inside a "lift shaft":
 *
 *   top of the shaft     its own place (default), the top of the page,
 *                        or the bottom of a chosen element (a Scroll
 *                        zone marker, a title...)
 *   bottom of the shaft  the end of the page (default), above the site
 *                        footer (0.47.95), or the top of a chosen element
 *
 * and never over another element: anything above or below it whose
 * width overlaps its own (a field in the same columns) narrows the shaft
 * to the space between them, so it only travels where its columns are
 * free. On small screens (under 768px) it stays in place unless asked.
 *
 * It is moved with a transform, so the page's layout does not change;
 * the element's own place keeps its space.
 *
 * 0.49.26 - Smooth. A transform set after each scroll step lags the
 * page by a frame, so the element shook while it followed the screen.
 * Now, while it follows the screen, its content box (data-float-body)
 * is fixed to the screen and the browser keeps it there; the cell
 * keeps its height so nothing below moves. Where the shaft stops it,
 * or at its own place, the box moves with the page again (a transform,
 * which scrolls with the page and so never lags). A cell without that
 * box (an older renderer) is moved with a transform as before.
 *
 * Cells carry data-el-cell (every element's grid cell); a floating one
 * also carries data-float and its settings (floatAttrs). */

const SMALL = 768;
const SPACING = 16; // kept between the element and what stops it

export function floatAttrs(el) {
  if (!el || !el.float) return {};
  const a = { 'data-float': '' };
  if (el.float_from && el.float_from !== 'self') a['data-float-from'] = String(el.float_from);
  if (el.float_to && el.float_to !== 'end') a['data-float-to'] = String(el.float_to);
  const gap = Number(el.float_gap);
  a['data-float-gap'] = String(Number.isFinite(gap) && gap >= 0 ? gap : 16);
  if (el.float_small) a['data-float-small'] = '';
  return a;
}

function docRect(node, sy) {
  const r = node.getBoundingClientRect();
  return { top: r.top + sy, bottom: r.bottom + sy, left: r.left, right: r.right, height: r.height };
}

function byId(eid) {
  if (!eid) return null;
  try {
    return document.querySelector(`[data-element-id="${CSS.escape(eid)}"]`);
  } catch {
    return null;
  }
}

export function layoutFloating() {
  if (typeof document === 'undefined') return;
  const floats = Array.from(document.querySelectorAll('[data-float]'));
  if (floats.length === 0) return;
  const sy = window.scrollY || window.pageYOffset || 0;
  const vh = window.innerHeight;
  const small = window.innerWidth < SMALL;
  const cells = Array.from(document.querySelectorAll('[data-el-cell]'));
  for (const el of floats) {
    const body = el.querySelector(':scope > [data-float-body]');
    if (body) { placeBody(el, body, sy, vh, small, cells); continue; }
    const shiftNow = Number(el.dataset.floatShift || 0);
    const set = (shift) => {
      const s = Math.round(shift);
      if (s === Math.round(shiftNow)) return;
      el.dataset.floatShift = String(s);
      el.style.transform = s ? `translateY(${s}px)` : '';
      el.style.position = s ? 'relative' : '';
      el.style.zIndex = s ? '3' : '';
    };
    if (small && !('floatSmall' in el.dataset)) { set(0); continue; }
    const r = el.getBoundingClientRect();
    if (r.height === 0) { set(0); continue; }
    const natTop = r.top + sy - shiftNow;
    const h = r.height;
    const natBottom = natTop + h;
    const gap = Number(el.dataset.floatGap || 16);

    // The shaft.
    let upper = natTop;
    const from = el.dataset.floatFrom;
    if (from === 'top') upper = 0;
    else if (from) {
      const n = byId(from);
      if (n) upper = Math.min(natTop, docRect(n, sy).bottom);
    }
    let lower = Math.max(document.documentElement.scrollHeight, natBottom);
    const to = el.dataset.floatTo;
    if (to === 'footer') {
      /* 0.47.95 - Above the site footer: the first <footer> below the
         element (none on the page: the end of the page). */
      const f = Array.from(document.querySelectorAll('footer'))
        .filter(n => !el.contains(n))
        .map(n => docRect(n, sy))
        .filter(rc => rc.height > 0 && rc.top >= natBottom - 1)
        .sort((x, y) => x.top - y.top)[0];
      if (f) lower = Math.max(natBottom, f.top - SPACING);
    } else if (to) {
      const n = byId(to);
      if (n) lower = Math.max(natBottom, docRect(n, sy).top);
    }

    // What is in its columns, above and below, narrows the shaft.
    for (const c of cells) {
      if (c === el || el.contains(c) || c.contains(el)) continue;
      if ('float' in c.dataset) continue;
      const o = docRect(c, sy);
      if (o.height === 0) continue;
      if (o.right <= r.left + 1 || o.left >= r.right - 1) continue;
      if (o.bottom <= natTop + 1) upper = Math.max(upper, o.bottom + SPACING);
      else if (o.top >= natBottom - 1) lower = Math.min(lower, o.top - SPACING);
    }
    if (lower - upper < h) { set(0); continue; }

    // Where the screen wants it: at its own place when that is on
    // screen, else just inside the top (or the bottom) of the screen.
    const viewTop = sy + gap;
    const viewBottom = sy + vh - gap;
    let top = natTop;
    if (top < viewTop) top = viewTop;
    else if (top + h > viewBottom && h <= viewBottom - viewTop) top = viewBottom - h;
    top = Math.max(top, upper);
    top = Math.min(top, lower - h);
    set(top - natTop);
  }
}

/* The shaft (upper, lower) for an element whose natural place is
   natTop..natTop+h in the page, left..right on screen. */
function shaftOf(el, natTop, h, left, right, sy, cells) {
  const natBottom = natTop + h;
  let upper = natTop;
  const from = el.dataset.floatFrom;
  if (from === 'top') upper = 0;
  else if (from) {
    const n = byId(from);
    if (n) upper = Math.min(natTop, docRect(n, sy).bottom);
  }
  let lower = Math.max(document.documentElement.scrollHeight, natBottom);
  const to = el.dataset.floatTo;
  if (to === 'footer') {
    const f = Array.from(document.querySelectorAll('footer'))
      .filter(n => !el.contains(n))
      .map(n => docRect(n, sy))
      .filter(rc => rc.height > 0 && rc.top >= natBottom - 1)
      .sort((x, y) => x.top - y.top)[0];
    if (f) lower = Math.max(natBottom, f.top - SPACING);
  } else if (to) {
    const n = byId(to);
    if (n) lower = Math.max(natBottom, docRect(n, sy).top);
  }
  for (const c of cells) {
    if (c === el || el.contains(c) || c.contains(el)) continue;
    if ('float' in c.dataset) continue;
    const o = docRect(c, sy);
    if (o.height === 0) continue;
    if (o.right <= left + 1 || o.left >= right - 1) continue;
    if (o.bottom <= natTop + 1) upper = Math.max(upper, o.bottom + SPACING);
    else if (o.top >= natBottom - 1) lower = Math.min(lower, o.top - SPACING);
  }
  return { upper, lower };
}

function placeBody(el, body, sy, vh, small, cells) {
  const bs = body.style;
  const natural = () => {
    if (!el.dataset.floatMode) return;
    delete el.dataset.floatMode;
    bs.position = ''; bs.top = ''; bs.left = ''; bs.width = ''; bs.transform = ''; bs.zIndex = '';
    el.style.minHeight = '';
  };
  if (small && !('floatSmall' in el.dataset)) { natural(); return; }
  // The cell never moves: its place is the natural place.
  const cr = el.getBoundingClientRect();
  const cs = window.getComputedStyle(el);
  const padT = parseFloat(cs.paddingTop) || 0;
  const padL = parseFloat(cs.paddingLeft) || 0;
  const padR = parseFloat(cs.paddingRight) || 0;
  const h = body.getBoundingClientRect().height;
  if (h === 0) { natural(); return; }
  const natTop = cr.top + sy + padT;
  const gap = Number(el.dataset.floatGap || 16);
  const { upper, lower } = shaftOf(el, natTop, h, cr.left, cr.right, sy, cells);
  if (lower - upper < h) { natural(); return; }

  const viewTop = sy + gap;
  const viewBottom = sy + vh - gap;
  let top = natTop;
  let follow = false;
  if (top < viewTop) { top = viewTop; follow = true; }
  else if (top + h > viewBottom && h <= viewBottom - viewTop) { top = viewBottom - h; follow = true; }
  if (top < upper) { top = upper; follow = false; }
  if (top > lower - h) { top = lower - h; follow = false; }
  const shift = Math.round(top - natTop);
  if (shift === 0) { natural(); return; }

  // The cell keeps the height its content had.
  el.style.minHeight = `${Math.round(h + padT + (parseFloat(cs.paddingBottom) || 0))}px`;
  if (follow) {
    el.dataset.floatMode = 'fixed';
    bs.position = 'fixed';
    bs.top = `${Math.round(top - sy)}px`;
    bs.left = `${cr.left + padL}px`;
    bs.width = `${Math.max(0, cr.width - padL - padR)}px`;
    bs.transform = '';
  } else {
    el.dataset.floatMode = 'shift';
    bs.position = 'relative';
    bs.top = ''; bs.left = ''; bs.width = '';
    bs.transform = `translateY(${shift}px)`;
  }
  bs.zIndex = '3';
}

/* Keeps the floating elements placed: on scroll, on resize, and when
 * the page's content changes size. Returns the clean-up. */
export function watchFloating() {
  if (typeof window === 'undefined') return () => {};
  let frame = 0;
  const run = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(() => { frame = 0; layoutFloating(); });
  };
  window.addEventListener('scroll', run, { passive: true });
  window.addEventListener('resize', run);
  let ro = null;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(run);
    ro.observe(document.body);
  }
  run();
  return () => {
    window.removeEventListener('scroll', run);
    window.removeEventListener('resize', run);
    if (ro) ro.disconnect();
    if (frame) window.cancelAnimationFrame(frame);
  };
}
